import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import { useTimeout } from 'fulogics-ui-core/dist/layout/hooks/useTimeout';
import Popup from 'fulogics-ui-web/dist/layout/dx/Popup/Popup';
import style from './OrderLockTimerPopup.module.scss';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import { dateDiff, humanizeDuration } from 'fulogics-ui-core';
import * as ids from './inspectorOptions';
import moment from 'moment';

const oneSec = 1000;
const twoMin = oneSec * 60 * 2;

const humanize = (duration: number) =>
    humanizeDuration(duration, {
        units: ['m', 's'],
    });

const OrderLockTimerPopup = ({
    className,
    deadline,
    visibilityTime,
    onLockExpire,
    onBack,
    onContinue,
}: OrderLockTimerPopupProps) => {
    const { t } = useTranslation('OrderLockTimerPopup');
    const [visible, setVisible] = useState<boolean>(false);
    const [expired, setExpired] = useState<boolean>(false);
    const [baseTimeout, setBaseTimeout] = useState<number | null>(null);
    const [timer, setTimer] = useState<number>(visibilityTime || twoMin);

    const visibilityTimeout =
        !!baseTimeout && !!visibilityTime ? baseTimeout - visibilityTime : null;

    const handleContinue = () => {
        onContinue?.()?.then(() => {
            setVisible(false);
            setTimer(visibilityTime || twoMin);
        });
    };

    useEffect(() => {
        if (deadline) {
            const diff = dateDiff(
                moment(deadline).toISOString(),
                new Date().toISOString()
            );

            setBaseTimeout(diff);
        }
    }, [deadline]);

    useTimeout(() => {
        setExpired(true);
        onLockExpire?.();
    }, baseTimeout);

    useTimeout(() => {
        setVisible(true);
    }, visibilityTimeout);

    useEffect(() => {
        if (visibilityTimeout !== null) {
            if (!visible) {
                setTimer(visibilityTime || twoMin);
            } else {
                const interval = setInterval(() => {
                    setTimer((x) => x - 1000);
                }, oneSec);

                return () => clearInterval(interval);
            }
        }
    }, [visible]);

    return (
        <>
            {visible && (
                <Helmet>
                    <title>
                        {expired
                            ? t('expired.title')
                            : t('info.countDown', {
                                  timer: humanize(timer),
                              })}
                    </title>
                </Helmet>
            )}
            <Popup visible={visible} inspectorId={ids.OrderLockTimerPopupId}>
                {expired ? (
                    <div className={classNames(style.container, className)}>
                        <h3>{t('expired.title')}</h3>
                        <div>{t('expired.subtitle')}</div>
                        <Button
                            className={classNames(style.backButton)}
                            inspectorId={ids.OrderLockTimerPopupBackButtonId}
                            onClick={onBack}
                        />
                    </div>
                ) : (
                    <div className={classNames(style.container, className)}>
                        <h3>{t('info.title')}</h3>
                        <div>{t('info.subtitle')}</div>
                        <h1 className={classNames(style.counter)}>{humanize(timer)}</h1>
                        <Button
                            className={classNames(style.continueButton)}
                            inspectorId={ids.OrderLockTimerPopupContinueButtonId}
                            onClick={handleContinue}
                        />
                        <Button
                            inspectorId={ids.OrderLockTimerPopupBackAndSaveButtonId}
                            onClick={onBack}
                        />
                    </div>
                )}
            </Popup>
        </>
    );
};

OrderLockTimerPopup.defaultProps = {
    visibilityTime: 1000 * 60 * 2,
};

export type OrderLockTimerPopupProps = {} & OrderLockTimerPopupOptions;

export interface OrderLockTimerPopupOptions {
    className?: string;
    deadline?: Date | number;
    visibilityTime?: number;
    //
    onLockExpire?: () => void;
    onBack?: () => void;
    onContinue?: () => Promise<void> | undefined;
}

export default withInspector<OrderLockTimerPopupProps>(OrderLockTimerPopup, {});
